import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, Store, Warehouse } from "lucide-react";

const Row = ({ label, value, testId, className = "" }) => (
  <div className="flex justify-between text-xs py-0.5" data-testid={testId}>
    <span className="text-[#6B7280]">{label}</span>
    <span className={`font-mono text-[#4A4A4A] ${className}`}>{value}</span>
  </div>
);

export const BatchReconciliation = ({ batch }) => {
  const [open, setOpen] = useState(false);
  const rec = batch.reconciliation;
  const breakdown = batch.sales_breakdown || [];
  if (!rec && breakdown.length === 0) return null;

  const diff = rec?.difference ?? 0;
  const missing = rec?.has_inventory_data && diff < 0;
  const extra = rec?.has_inventory_data && diff > 0;

  return (
    <div className="mt-3 ml-7" data-testid={`batch-reconciliation-${batch.id}`}>
      {rec?.stock_out_alert && (
        <div className="mb-2 flex items-start gap-2 rounded-md border border-[#EF4444] bg-[#EF4444]/10 p-2 text-xs text-[#B91C1C]" data-testid={`stock-out-alert-${batch.id}`}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span><b>Inventario agotado:</b> ya no hay piezas en Odoo ni Shopify, aunque las ventas digan que faltan {rec.expected_remaining}. Reordena o revisa el conteo.</span>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between rounded-md bg-white/50 border border-black/5 px-2 py-1.5 text-xs text-[#4A4A4A] hover:bg-white/80 transition-colors"
        data-testid={`toggle-reconciliation-${batch.id}`}
      >
        <span className="flex items-center gap-1.5">
          <Warehouse className="w-3.5 h-3.5 text-[#6366F1]" />
          {rec?.has_inventory_data ? (
            <>En inventario: <b data-testid={`stock-on-hand-${batch.id}`}>{rec.stock_on_hand}</b></>
          ) : "Sin datos de inventario"}
          {missing && <span className="text-[#EF4444] font-semibold" data-testid={`unaccounted-${batch.id}`}>· {Math.abs(diff)} sin rastro</span>}
          {extra && <span className="text-[#F59E0B] font-semibold" data-testid={`extra-stock-${batch.id}`}>· {diff} de más</span>}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-2 space-y-3 rounded-md bg-white/60 border border-black/5 p-2">
          <div>
            <p className="text-xs font-semibold text-[#4A4A4A] mb-1 flex items-center gap-1"><Store className="w-3.5 h-3.5" /> Vendidas por tienda / canal</p>
            {breakdown.length === 0 ? <p className="text-xs text-[#9CA3AF]">Sin ventas registradas</p> :
              breakdown.map((b, i) => <Row key={b.channel} label={b.channel} value={b.qty} testId={`sales-channel-${batch.id}-${i}`} />)}
          </div>
          {rec && (
            <div>
              <p className="text-xs font-semibold text-[#4A4A4A] mb-1">Conciliación</p>
              <Row label="Piezas que deberían quedar (según ventas)" value={rec.expected_remaining} testId={`expected-remaining-${batch.id}`} />
              {rec.stock_by_location.map((l, i) => <Row key={l.location} label={l.location} value={l.qty} testId={`stock-loc-${batch.id}-${i}`} />)}
              <Row label="Total en inventario" value={rec.stock_on_hand} className="font-semibold" />
              {rec.has_inventory_data && (
                <Row
                  label={diff < 0 ? "Sin rastro (no se cobraron ni están en inventario)" : diff > 0 ? "De más en inventario (falta ajustar)" : "Cuadra"}
                  value={diff === 0 ? "✓" : Math.abs(diff)}
                  className={diff < 0 ? "text-[#EF4444] font-semibold" : diff > 0 ? "text-[#F59E0B] font-semibold" : "text-[#10B981]"}
                  testId={`reconciliation-diff-${batch.id}`}
                />
              )}
              <p className="text-[10px] text-[#9CA3AF] mt-1">Los traspasos entre Shopify y Odoo no afectan: se suman ambos. Si no cuadra, haz conteo físico y ajústalo en Inventario.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
